import { Fragment, ReactNode } from 'react';
import { ArrowUpDown, Check, RotateCcw } from 'lucide-react';
import { SortableCardList } from './SortableCardList';

interface ReorderableSectionsProps<Id extends string> {
  /** Every box's content by id. */
  sections: Record<Id, ReactNode>;
  /** Box ids in the user's order. */
  order: Id[];
  /** Boxes not shown right now (e.g. Tags before a company is picked). */
  hidden?: Partial<Record<Id, boolean>>;
  /** Box names for the drag handles. */
  labels: Record<Id, string>;
  isReordering: boolean;
  isCustom: boolean;
  isMobile: boolean;
  onReorder: (visibleIds: string[]) => void;
  onReset: () => void;
  onDone: () => void;
  /** Spacing between boxes in Reorder mode, to match the form's normal layout. */
  listClassName?: string;
}

/**
 * A form's boxes in the user's order. In Reorder mode each box gets a grip to drag it, plus a
 * bar with Reset and Done; otherwise the boxes render plainly with no extra space.
 */
export function ReorderableSections<Id extends string>({
  sections,
  order,
  hidden = {},
  labels,
  isReordering,
  isCustom,
  isMobile,
  onReorder,
  onReset,
  onDone,
  listClassName = 'space-y-1.5',
}: ReorderableSectionsProps<Id>) {
  const visibleIds = order.filter((id) => !hidden[id]);

  if (!isReordering) {
    return <>{visibleIds.map((id) => <Fragment key={id}>{sections[id]}</Fragment>)}</>;
  }

  return (
    <>
      <div className="sticky top-0 z-[1] flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2">
        <p className="flex-1 text-xs text-primary-800">Drag a box by its grip to move it.</p>
        <button
          type="button"
          onClick={onReset}
          disabled={!isCustom}
          className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </button>
        <button
          type="button"
          onClick={onDone}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-primary-600 px-3 text-xs font-semibold text-white"
        >
          <Check className="h-3.5 w-3.5" />
          Done
        </button>
      </div>
      <SortableCardList
        className={listClassName}
        handleSize={isMobile ? 'touch' : 'default'}
        cards={visibleIds.map((id) => ({ id, label: labels[id], node: sections[id] }))}
        onReorder={onReorder}
      />
    </>
  );
}

interface ReorderButtonProps {
  isReordering: boolean;
  onToggle: () => void;
}

/** "Reorder" toggle: next to the status in a form header on desktop, under the ✕ on phones. */
export function ReorderButton({ isReordering, onToggle }: ReorderButtonProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex h-6 items-center gap-1 rounded-lg px-2 text-xs font-medium transition-colors ${isReordering ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
      aria-pressed={isReordering}
    >
      <ArrowUpDown className="h-3.5 w-3.5" />
      Reorder
    </button>
  );
}
